// src/components/SplitView.tsx
// Resizable two-panel layout for viewing detail alongside list/board views.
// Uses react-resizable-panels for the drag handle.

import { useEffect, useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SplitViewProps {
  /** Content for the main (left) panel */
  children: ReactNode;
  /** Content for the detail (right) panel - null means closed */
  detail: ReactNode | null;
  /** Called when the detail panel should close */
  onClose: () => void;
  /** Minimum size of main panel in percentage (default: 30) */
  minMainSize?: number;
  /** Default size of detail panel in percentage (default: 45) */
  defaultDetailSize?: number;
  /**
   * Whether this component supplies the close affordance (default: true).
   * A detail panel that draws its own header — with a close of its own — passes
   * `false`: on desktop the two buttons otherwise land on the same corner and
   * overlap, and on mobile the close bar stacks a second header above the
   * panel's. Either way the panel's own header becomes the only chrome.
   */
  showClose?: boolean;
}

// ---------------------------------------------------------------------------
// Close icon
// ---------------------------------------------------------------------------

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SplitView({
  children,
  detail,
  onClose,
  minMainSize = 30,
  defaultDetailSize = 45,
  showClose = true,
}: SplitViewProps) {
  const { t } = useI18n();

  const root = useRef<HTMLDivElement>(null);
  const groupId = useId();
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const measure = () => setWide(element.clientWidth >= 768);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && detail && !event.defaultPrevented && root.current?.getClientRects().length) {
      onClose();
    }
  }, [detail, onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const preview = detail ? (
    <div className="relative flex h-full min-h-0 flex-col bg-background" role="complementary" aria-label={t('task.splitDetail')}>
      {showClose && (
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-2">
          <span className="text-sm font-medium">{t('task.details')}</span>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"><XIcon className="h-5 w-5" /></button>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">{detail}</div>
    </div>
  ) : null;

  return (
    <div ref={root} className="relative h-full min-h-0 w-full min-w-0">
      {/* Keep the main subtree at the same React position, open or closed.
          CSS-hidden duplicate trees would still fetch, subscribe and mutate. */}
      <Group orientation="horizontal" id={groupId} className="h-full w-full">
        <Panel id="main" minSize={`${minMainSize}%`} defaultSize={`${100 - defaultDetailSize}%`}>
          <div className="h-full overflow-auto">{children}</div>
        </Panel>
        {detail && wide && (
          <>
            <Separator className={cn('w-1 shrink-0 self-stretch bg-border hover:bg-primary focus-visible:bg-primary cursor-col-resize')} />
            <Panel id="detail" minSize="25%" defaultSize={`${defaultDetailSize}%`}>
              {preview}
            </Panel>
          </>
        )}
      </Group>
      {detail && !wide && (
        <div className="fixed inset-0 z-50 bg-background md:absolute">{preview}</div>
      )}
    </div>
  );
}

export default SplitView;
